import { useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { creances, type Index } from '../../analysis/donnees'
import type { Creance } from '../../analysis/repartition'
import { somme } from '../../domain/argent'
import { anneeDe, aujourdhui, formatJour } from '../../domain/temps'
import type { Id } from '../../domain/types'
import { Carte, Entete, IconeChevron, IconeDocument, Montant, Puce } from '../composants'
import { useDonnees } from '../donnees'

export function PageRemboursements() {
  const { ix } = useDonnees()
  const [params, setParams] = useSearchParams()
  const toutes = params.get('toutes') === '1'
  const filtreTiers = params.get('tiers')

  const groupes = useMemo(() => {
    const m = new Map<Id, Creance[]>()
    for (const c of creances(ix)) {
      if (!toutes && c.statut === 'soldee') continue
      if (filtreTiers && c.part.tiersId !== filtreTiers) continue
      m.set(c.part.tiersId, [...(m.get(c.part.tiersId) ?? []), c])
    }
    return [...m.entries()]
      .map(([tiersId, liste]) => ({
        tiersId,
        liste: liste.sort((a, b) => date(ix, b).localeCompare(date(ix, a))),
        restant: somme(liste.map((c) => Math.max(0, c.restant))),
      }))
      .sort((a, b) => b.restant - a.restant)
  }, [ix, toutes, filtreTiers])

  const jour = aujourdhui()

  return (
    <>
      <Entete titre="À recevoir" sous="Remboursements et parts refacturées" retour />
      <main className="contenu">
        <div className="boutons">
          <button type="button" className="btn btn--petit" aria-pressed={toutes} onClick={() => setParams(toutes ? {} : { toutes: '1' }, { replace: true })}>
            {toutes ? 'Masquer les soldées' : 'Afficher les soldées'}
          </button>
          {filtreTiers && (
            <button type="button" className="btn btn--petit" onClick={() => setParams({}, { replace: true })}>Tous les tiers</button>
          )}
        </div>

        {groupes.length === 0 && <p className="vide">Rien à recevoir. Tout est réglé.</p>}

        {groupes.map((g) => {
          const tiers = ix.tiersParId.get(g.tiersId)
          return (
            <Carte
              key={g.tiersId}
              titre={tiers?.nom ?? '—'}
              action={<span className="gras"><Montant valeur={g.restant} /></span>}
            >
              <ul className="liste">
                {g.liste.map((c) => {
                  const op = ix.operationParId.get(c.part.operationId)!
                  return (
                    <li key={c.part.id}>
                      <Link to={`/operations/${op.id}`} className="item">
                        <div className="grandir">
                          <div className="item__principal">{op.libelle}</div>
                          <div className="item__secondaire">
                            {formatJour(op.date)} · dû <Montant valeur={c.du} />
                            {c.recu > 0 && <> · reçu <Montant valeur={c.recu} /></>}
                          </div>
                        </div>
                        <Puce ton={c.statut === 'soldee' ? 'ok' : c.statut === 'partielle' ? 'info' : 'alerte'}>
                          {c.statut === 'soldee' ? 'soldé' : c.statut === 'partielle' ? 'partiel' : 'attendu'}
                        </Puce>
                        <span className="chevron"><IconeChevron /></span>
                      </Link>
                    </li>
                  )
                })}
              </ul>
              {tiers?.type === 'personne' && g.restant > 0 && (
                <Link to={`/justificatif?tiers=${g.tiersId}&du=${anneeDe(jour) - 1}-01-01&au=${jour}`} className="btn btn--bloc" style={{ marginTop: 10 }}>
                  <IconeDocument /> Justificatif pour {tiers.nom}
                </Link>
              )}
            </Carte>
          )
        })}
      </main>
    </>
  )
}

function date(ix: Index, c: Creance): string {
  return ix.operationParId.get(c.part.operationId)?.date ?? ''
}
